/* eslint-disable camelcase */
import React, { useState } from "react"
import { Box, Typography, IconButton, TextField, Button, Grid, InputAdornment, Tooltip } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import DeleteIcon from "@mui/icons-material/Delete"
import CalculateIcon from "@mui/icons-material/Calculate"
import Swal from "sweetalert2"

// Helper monetario
const formatMoneda = (valor) => {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(valor || 0)
}

const TablaPlanillaCobro = ({ facturasCobrar, setFacturasCobrar }) => {
  const [montoProrratear, setMontoProrratear] = useState("")

  // =========================================================================
  // ACTUALIZACIÓN MANUAL DE ABONO Y RECÁLCULO DE MORA
  // Equivalente al evento fpSDetalle_Change de VB6
  // =========================================================================
  const handleAbonoChange = (id, nuevoAbono) => {
    const abonoVal = parseFloat(nuevoAbono)

    setFacturasCobrar((prev) =>
      prev.map((fac) => {
        if (`${fac.facnumfac}-${fac.facsecuen}` === id) {
          // Validaciones restrictivas
          let abonoFinal = isNaN(abonoVal) || abonoVal < 0 ? 0 : abonoVal
          if (abonoFinal > fac.saldo_neto) {
            abonoFinal = fac.saldo_neto
          }

          // Recálculo dinámico de la mora proporcional al abono ingresado
          // Fórmula: (Abono / Saldo Neto Original) * Mora Calculada Original
          const moraProporcional = fac.saldo_neto > 0 ? (abonoFinal / fac.saldo_neto) * fac.mora_calculada : 0

          return {
            ...fac,
            abono: abonoFinal,
            mora_dinamica: Number(moraProporcional.toFixed(2)),
          }
        }
        return fac
      }),
    )
  }

  // =========================================================================
  // PRORRATEO DE VALORES
  // Equivalente al evento txtValor_KeyPress de VB6
  // =========================================================================
  const ejecutarProrrateo = () => {
    const montoDisponible = parseFloat(montoProrratear)

    if (isNaN(montoDisponible) || montoDisponible <= 0) {
      Swal.fire("Error", "Ingrese un monto válido para prorratear.", "error")
      return
    }

    // Calcula la deuda total (Solo capital neto)
    const totalDeudaNeta = facturasCobrar.reduce((acc, fac) => acc + fac.saldo_neto, 0)

    if (montoDisponible > totalDeudaNeta) {
      Swal.fire(
        "Aviso",
        `El monto ingresado ($${montoDisponible}) supera el total de la deuda capital ($${totalDeudaNeta}).`,
        "warning",
      )
      return
    }

    let montoRestante = montoDisponible

    // Distribuimos el pago calculando el porcentaje que representa cada cuota del total
    const nuevasFacturas = facturasCobrar.map((fac, index) => {
      const porcentaje = fac.saldo_neto / totalDeudaNeta
      let abonoAsignado = Number((porcentaje * montoDisponible).toFixed(2))

      // Ajuste de centavos en la última cuota
      if (index === facturasCobrar.length - 1) {
        abonoAsignado = Number(montoRestante.toFixed(2))
      }

      montoRestante -= abonoAsignado

      // Recalcular Mora
      const moraProporcional = fac.saldo_neto > 0 ? (abonoAsignado / fac.saldo_neto) * fac.mora_calculada : 0

      return {
        ...fac,
        abono: abonoAsignado,
        mora_dinamica: Number(moraProporcional.toFixed(2)),
      }
    })

    setFacturasCobrar(nuevasFacturas)
    setMontoProrratear("")
  }

  // =========================================================================
  // ELIMINAR CUOTA DE LA PLANILLA
  // =========================================================================
  const handleEliminar = (id) => {
    setFacturasCobrar((prev) => prev.filter((fac) => `${fac.facnumfac}-${fac.facsecuen}` !== id))
  }

  // =========================================================================
  // COLUMNAS DEL DATAGRID
  // =========================================================================
  const columns = [
    {
      field: "facnumfac",
      headerName: "Documento",
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="primary.main">
          {params.value}
        </Typography>
      ),
    },
    { field: "facsecuen", headerName: "Cuota", width: 70, align: "center", headerAlign: "center" },
    { field: "factipo", headerName: "Tipo", width: 70, align: "center", headerAlign: "center" },
    {
      field: "saldo_neto",
      headerName: "Saldo Base",
      width: 100,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => formatMoneda(params.value),
    },
    {
      field: "abono",
      headerName: "Abono (Capital)",
      width: 140,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <TextField
          variant="outlined"
          size="small"
          type="number"
          value={params.row.abono === 0 ? "" : params.row.abono} // UX: Limpia si es 0
          onChange={(e) => handleAbonoChange(`${params.row.facnumfac}-${params.row.facsecuen}`, e.target.value)}
          inputProps={{
            min: 0,
            max: params.row.saldo_neto,
            style: { textAlign: "right", fontWeight: "bold" },
          }}
          sx={{ width: "100%", bgcolor: "#fff" }}
        />
      ),
    },
    {
      field: "mora_dinamica",
      headerName: "Mora a Cobrar",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Typography
          variant="body2"
          color={params.row.mora_dinamica > 0 ? "error.main" : "textSecondary"}
          fontWeight="bold"
        >
          {formatMoneda(params.row.mora_dinamica ?? params.row.mora_calculada)}
        </Typography>
      ),
    },
    {
      field: "total_fila",
      headerName: "Total Cuota",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const totalFila = Number(params.row.abono || 0) + Number(params.row.mora_dinamica || 0)
        return (
          <Typography variant="body2" fontWeight="bold">
            {formatMoneda(totalFila)}
          </Typography>
        )
      },
    },
    {
      field: "acciones",
      headerName: "Acciones",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="Quitar de la planilla">
          <IconButton
            color="error"
            size="small"
            onClick={() => handleEliminar(`${params.row.facnumfac}-${params.row.facsecuen}`)}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  // Cálculo de Subtotales Visuales de la Tabla
  const totalCapitalCobrar = facturasCobrar.reduce((acc, fac) => acc + Number(fac.abono || 0), 0)
  const totalMoraCobrar = facturasCobrar.reduce((acc, fac) => acc + Number(fac.mora_dinamica || 0), 0)

  return (
    <Box>
      {/* Controles Superiores: Prorrateo Automático */}
      {facturasCobrar.length > 0 && (
        <Grid container spacing={2} alignItems="center" mb={2} sx={{ bgcolor: "#f5f7fa", p: 1.5, borderRadius: 2 }}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" fontWeight="bold" color="primary">
              Herramienta de Prorrateo Automático:
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Distribuya un pago parcial entre todas las cuotas.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Monto a Prorratear"
              type="number"
              value={montoProrratear}
              onChange={(e) => setMontoProrratear(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ejecutarProrrateo()}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<CalculateIcon />}
              onClick={ejecutarProrrateo}
              sx={{ fontWeight: "bold" }}
            >
              Aplicar Prorrateo
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Tabla de Planilla */}
      <DataGrid
        rows={facturasCobrar}
        columns={columns}
        getRowId={(row) => `${row.facnumfac}-${row.facsecuen}`}
        disableRowSelectionOnClick
        hideFooterPagination
        hideFooter
        sx={{
          minHeight: 250,
          "& .MuiDataGrid-cell": { fontSize: "0.85rem" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: "#e1f5fe", fontWeight: "bold" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "#f1f8e9" },
        }}
        localeText={{ noRowsLabel: "No hay cuotas seleccionadas para cobrar." }}
      />

      {/* Fila de Totales */}
      {facturasCobrar.length > 0 && (
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          p={2}
          bgcolor="#eeeeee"
          borderRadius={1}
        >
          <Box mr={4} textAlign="right">
            <Typography variant="caption" color="textSecondary">
              Total Capital Abono
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              {formatMoneda(totalCapitalCobrar)}
            </Typography>
          </Box>
          <Box mr={4} textAlign="right">
            <Typography variant="caption" color="error">
              Total Interés Mora
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold" color="error">
              {formatMoneda(totalMoraCobrar)}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="caption" color="primary">
              Total a Cobrar
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {formatMoneda(totalCapitalCobrar + totalMoraCobrar)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default TablaPlanillaCobro
